import { useState } from "react";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, Send, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { submitForm, errMessage } from "@/lib/api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Global waitlist capture (email only). Saves to the backend.
export const WaitlistForm = ({
  testIdPrefix = "waitlist",
  className = "",
  buttonLabel = "Join the waitlist",
  source = "website",
  dark = false,
}) => {
  const [email, setEmail] = useState("");
  const [joined, setJoined] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email.trim())) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await submitForm("/waitlist", { email: email.trim(), source });
      setJoined(true);
      toast.success(
        res?.duplicate ? "You're already on the list!" : "You're on the waitlist!",
        { description: "We'll notify you the moment we go live." }
      );
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (joined) {
    return (
      <div
        data-testid={`${testIdPrefix}-success`}
        className={cn(
          "flex items-center gap-2 rounded-lg border border-halalgo-green-200 bg-halalgo-green-50 px-4 py-3 text-sm font-medium text-halalgo-green-800",
          className
        )}
      >
        <CheckCircle2 className="h-5 w-5 shrink-0" />
        Salamat! You're on the waitlist — we'll be in touch soon.
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className={cn("flex w-full max-w-md flex-col gap-2 sm:flex-row", className)}
      data-testid={`${testIdPrefix}-form`}
      noValidate
    >
      <Input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@email.com"
        aria-label="Email address"
        data-testid={`${testIdPrefix}-email`}
        className={cn("h-11 flex-1", dark ? "bg-white/95 border-transparent" : "bg-white")}
      />
      <Button
        type="submit"
        disabled={submitting}
        data-testid={`${testIdPrefix}-submit`}
        className="h-11 bg-halalgo-green-600 px-5 text-white shadow-lift-sm transition-colors hover:bg-halalgo-green-700 active:scale-[0.98]"
      >
        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        {buttonLabel}
      </Button>
    </form>
  );
};
